import { Grid, Paper, Typography, Tooltip, Box  } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';

import { Field } from 'formik';
import { compose } from 'recompose';
import { isEqual, values } from 'lodash';
import PropTypes from 'prop-types';
import React, { Component } from 'react';

import { Checkbox, TextField, ToggleSelect } from 'modules/intake/form';
import AdvancedSearchComponent from 'modules/common/advancedSearch.component';
import ExpansionPanel from 'modules/common/expansionPanel.component';
import ExpansionPanelDetails from 'modules/common/expansionPanelDetails.component';
import ExpansionPanelSummary from 'modules/common/expansionPanelSummary.component';
import InputContext from 'modules/intake/form/inputContext';
import Log from 'utilities/log';

import { DestinationInformation, FinalMoveBenefits, HouseHuntingBenefits } from 'modules/intake/models';
import {
  PARTNER_ID_KEY,
  PARTNER_NAME_KEY,
  PARTNER_SEARCH_COLUMNS,
  PARTNER_SEARCH_COLUMN_EXTENSIONS,
  findPartnerName,
} from 'modules/common/partnerSearchInfo';
import { YES_NO_OPTIONS } from 'utilities/common';
import ConfirmationDialog from 'modules/common/confirmationDialog.component';
import styles from 'modules/intake/styles';
import WarningIcon from '@material-ui/icons/Warning'

const AREA_TOUR_FIELDS = [
  'newDestination.rentingAreaTourAgentId',
  'newDestination.rentingAreaTourInitiationDate',
  'newDestination.rentingAreaTourCanceledDate',
  'newDestination.rentingAreaTourCompletedDate',
];

// Mirrors the Preferred Agent fields on the Destination Services: Buyer page (buyerPreferencesCard).
const PREFERRED_AGENT_FIELDS = [
  'newDestination.purchasingPreferredAgentName',
  'newDestination.purchasingPreferredAgency',
  'newDestination.purchasingPreferredAgencyEmail',
  'newDestination.purchasingPreferredAgencyPhone',
];

class NewDestinationForm extends Component {
  static contextType = InputContext;

  constructor(props) {
    super(props);

    this.state = {
      showConfirmation: false,
      // Seeded from the loaded data on mount; manual toggles take over from there.
      areaTourChecked: DestinationInformation.hasAreaTourData(props.newDestination),
    };
  }

  shouldComponentUpdate(nextProps, nextState) {
    return !isEqual(this.props.newDestination, nextProps.newDestination) ||
      !isEqual(this.state, nextState) ||
      !isEqual(this.props.eeIntakeForm, nextProps.eeIntakeForm) ||
      !isEqual(this.props.moveTypeId, nextProps.moveTypeId) ||
      !isEqual(this.props.partners, nextProps.partners) ||
      this.props.isReadOnly !== nextProps.isReadOnly;
  }

  // force refresh or useeffect in order to look at the state of the movetypeid

  renderHeader() {
    const { moveTypeId, classes, newDestination, handleChange, handleBlur, destinationInformationComment } = this.props;
    return (
      <Grid container alignItems="center" spacing={2}>
        <Grid item>
          <Typography variant="h3">New Destination Information</Typography>
        </Grid>
        {newDestination.isDestinationServiceInitiated && destinationInformationComment && (
          <Grid item>
            <Tooltip title={<Typography variant="body1">Transferee has left a comment regarding Destination Information</Typography>}>
              <Box
                sx={{
                  height: '30px',
                  width: '30px',
                  backgroundColor: '#f0b11d',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: 1
                }}
              >
                <WarningIcon sx={{ color: 'white' }} />
              </Box>
            </Tooltip>
          </Grid>
        )}
        <Grid item>
          <Field
            component={Checkbox}
            className={classes.headerToggle}
            name="newDestination.isDestinationServiceInitiated"
            value={newDestination.isDestinationServiceInitiated}
            onChange={event => {
              // this value is the current state, not what it will change to if we allow it
              if (event.target.value === 'checked') {
                this.setState({ showConfirmation: true });
              } else {
                if (!newDestination.isDestinationServiceInitiated && (moveTypeId === 0 || moveTypeId === 7)) {
                  this.setState({ showChangeMoveType: true });
                } else {
                  handleChange(event);
                }
              }
            }}
            onBlur={handleBlur}
            label="Initiate Service"
          />
        </Grid>
      </Grid>
    );
  }

  renderAutomaticInitiated() {
    const { classes, newDestination, handleBlur, destinationInformationComment } = this.props;

    if (!newDestination.isDestinationServiceInitiated) {
      newDestination.isDestinationServiceInitiated = true;
      this.props.setFieldValue(newDestination, new DestinationInformation(), true);
    }

    return (
      <Grid container alignItems="center" spacing={2}>
        <Grid item>
          <Typography variant="h3">New Destination Information</Typography>
        </Grid>
        {newDestination.isDestinationServiceInitiated && destinationInformationComment && (
          <Grid item>
            <Tooltip title={<Typography variant="body1">"Transferee has left a comment regarding Destination Information"</Typography>}>
              <Box
                sx={{
                  height: '30px',
                  width: '30px',
                  backgroundColor: '#f0b11d',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: 1
                }}
              >
                <WarningIcon sx={{ color: 'white' }} />
              </Box>
            </Tooltip>
          </Grid>
        )}
        <Grid item>
          <Field
            component={Checkbox}
            className={classes.headerToggle}
            name="newDestination.isDestinationServiceInitiated"
            value={newDestination.isDestinationServiceInitiated}
            disabled={true}
            onBlur={handleBlur}
            label="Initiate Service"
          />
        </Grid>
      </Grid>
    );
  }

  handleDisableService = () => {
    this.props.setFieldValue('newDestination', new DestinationInformation(), false);
    this.props.setFieldValue('benefits.houseHunting', new HouseHuntingBenefits(), false);
    this.props.setFieldValue('benefits.finalMove', new FinalMoveBenefits(), false);
    this.setState({ showConfirmation: false, areaTourChecked: false });
  };

  handleAreaTourToggle = (event) => {
    const checked = Boolean(event.target.checked);
    this.setState({ areaTourChecked: checked });

    // Unchecking hides the fields and clears their values so the next save nulls them on the back end.
    if (!checked) {
      AREA_TOUR_FIELDS.forEach((fieldName) => this.props.setFieldValue(fieldName, '', false));
    }
  };

  // Unchecking hides the agent fields and clears their values so the next save nulls them on the back end.
  handlePreferredAgentToggle = (event) => {
    this.props.handleChange(event);

    if (!event.target.checked) {
      PREFERRED_AGENT_FIELDS.forEach((fieldName) => this.props.setFieldValue(fieldName, '', false));
    }
  };

  // The note field only shows while the toggle is Yes; anything else hides it and clears the value.
  handleSchoolRequestToggle = (noteFieldName) => (event) => {
    this.props.handleChange(event);

    if (event.target.value !== true) {
      this.props.setFieldValue(noteFieldName, '', false);
    }
  };

  // The search control reports a cleared selection as null or an empty array; the form stores '' for "no partner".
  // The stored field keeps its rentingAreaTourAgentId name on the wire but now holds the selected partner's vendorId.
  handleAreaTourPartnerChange = (partnerId) => {
    const value = partnerId === null || partnerId === undefined || Array.isArray(partnerId) ? '' : partnerId;
    this.props.setFieldValue('newDestination.rentingAreaTourAgentId', value, false);
    // The search control bypasses the intake form adapters, so notify the autosave listener the same way they do.
    this.context.onChange();
  };

  render() {
    Log.trace('RENDER', 'NewDestinationForm');
    const {
      classes,
      eeIntakeForm,
      newDestination,
      handleChange,
      handleBlur,
      isHhgInitiated,
      isTempLivingInitiated,
      partners,
      isReadOnly,
      metadata: { familiarWithAreaTypes, housePropertyTypes, preferenceTypes },
    } = this.props;
    const { showConfirmation, showChangeMoveType } = this.state;
    const partnerList = Array.isArray(partners) ? partners : [];
    const selectedAreaTourPartnerId = newDestination.rentingAreaTourAgentId;
    const hasSelectedAreaTourPartner = selectedAreaTourPartnerId !== '' && selectedAreaTourPartnerId !== null && selectedAreaTourPartnerId !== undefined;
    const selectedAreaTourPartnerName = findPartnerName(partnerList, selectedAreaTourPartnerId);
    // Show the partner's name once the list has loaded; until then fall back to the stored id so the field never looks empty.
    const areaTourPartnerDisplay = selectedAreaTourPartnerName || (hasSelectedAreaTourPartner ? String(selectedAreaTourPartnerId) : '');
    // Data arriving after mount (a reload or autosave response) can only turn the box on, never off.
    const areaTourChecked = this.state.areaTourChecked || DestinationInformation.hasAreaTourData(newDestination);
    return (
      <div className="form-group">
        <ExpansionPanel expanded={newDestination.isDestinationServiceInitiated}>
          {(isHhgInitiated || isTempLivingInitiated) &&
            <>
              <ExpansionPanelSummary>{this.renderAutomaticInitiated()}</ExpansionPanelSummary>
            </>
          }
          {!(isHhgInitiated || isTempLivingInitiated) &&
            <>
              <ExpansionPanelSummary>{this.renderHeader()}</ExpansionPanelSummary>
            </>
          }
          <ExpansionPanelDetails>
            <Grid container>
              <Grid item xs={12}>
                <Paper classes={{ root: 'card-root' }}>
                  <ExpansionPanel expanded={!!newDestination.isPurchasing}>
                    <ExpansionPanelSummary>
                      <div className="row align-center">
                        <Typography variant="h4">Purchasing</Typography>
                        <div>
                          <Field
                            component={ToggleSelect}
                            name="newDestination.isPurchasing"
                            value={newDestination.isPurchasing}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            options={YES_NO_OPTIONS}
                            label="Is Purchasing"
                            className={classes.headerToggle}
                          />
                          {eeIntakeForm?.record.isSubmitted && (eeIntakeForm?.record.isPurchasing != null) && (
                            <Typography style={{ color: 'green' }} className={classes.headerToggle}> ^ Sourced from EE</Typography>
                          )}
                        </div>
                      </div>
                    </ExpansionPanelSummary>
                    <ExpansionPanelDetails>
                      <Grid container spacing={1}>
                        <Grid item xs={12}>
                          <div className="row mb-1">
                            <div>
                              <Field
                                component={ToggleSelect}
                                name="newDestination.purchasingDestinationHomeTypeId"
                                value={newDestination.purchasingDestinationHomeTypeId}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                options={values(housePropertyTypes)}
                                variant="spread"
                                label="Property Type"
                                labelPlacement="top"
                              />
                              {eeIntakeForm?.record.isSubmitted && (eeIntakeForm?.record.purchasingDestinationHomeTypeId != null) && (
                                <Typography style={{ color: 'green' }}>^ Sourced from EE</Typography>
                              )}
                            </div>
                          </div>
                          <div className="row mb-1">
                            <div>
                              <Field
                                component={ToggleSelect}
                                name="newDestination.purchasingFamiliarWithAreaId"
                                value={newDestination.purchasingFamiliarWithAreaId}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                options={values(familiarWithAreaTypes)}
                                label="Familiar with Area?"
                              />
                              {eeIntakeForm?.record.isSubmitted && (eeIntakeForm?.record.purchasingFamiliarWithAreaId != null) && (
                                <Typography style={{ color: 'green' }}>^ Sourced from EE</Typography>
                              )}
                            </div>
                          </div>
                          <div className="row mb-1">
                            <Field
                              component={TextField}
                              name="newDestination.purchasingMinPrice"
                              label="Min Price"
                              value={newDestination.purchasingMinPrice}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              allowDecimal={true}
                              numeric
                              prefix="$"
                            />
                            <Field
                              component={TextField}
                              name="newDestination.purchasingMaxPrice"
                              label="Max Price"
                              value={newDestination.purchasingMaxPrice}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              allowDecimal={true}
                              numeric
                              prefix="$"
                            />
                          </div>
                          <div className="row mb-1">
                            <Field
                              component={TextField}
                              name="newDestination.purchasingBedroomsPreferred"
                              label="Bedroom Preference"
                              value={newDestination.purchasingBedroomsPreferred}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              numeric
                            />
                            <Field
                              component={TextField}
                              name="newDestination.purchasingBathroomsPreferred"
                              label="Bathroom Preference"
                              value={newDestination.purchasingBathroomsPreferred}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              numeric
                            />
                            <Field
                              component={TextField}
                              name="newDestination.purchasingSquareFootage"
                              label="Square Feet"
                              value={newDestination.purchasingSquareFootage}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              numeric
                            />
                          </div>
                          <div className="row mb-1">
                            <Field
                              component={Checkbox}
                              name="newDestination.purchasingHasPreferredAgent"
                              value={newDestination.purchasingHasPreferredAgent}
                              onChange={this.handlePreferredAgentToggle}
                              onBlur={handleBlur}
                              label="Preferred Agent"
                            />
                          </div>
                          {newDestination.purchasingHasPreferredAgent === true && (
                            <div className="row mb-1">
                              <Field
                                component={TextField}
                                name="newDestination.purchasingPreferredAgentName"
                                label="Preferred Agent Name"
                                value={newDestination.purchasingPreferredAgentName}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              />
                              <Field
                                component={TextField}
                                name="newDestination.purchasingPreferredAgency"
                                label="Preferred Agency"
                                value={newDestination.purchasingPreferredAgency}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              />
                              <Field
                                component={TextField}
                                name="newDestination.purchasingPreferredAgencyEmail"
                                label="Preferred Agency Email"
                                value={newDestination.purchasingPreferredAgencyEmail}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              />
                              <Field
                                component={TextField}
                                name="newDestination.purchasingPreferredAgencyPhone"
                                label="Preferred Agency Phone"
                                value={newDestination.purchasingPreferredAgencyPhone}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              />
                            </div>
                          )}
                          <div className="row mb-1">
                            <Field
                              component={ToggleSelect}
                              name="newDestination.purchasingFireplacePreferenceId"
                              value={newDestination.purchasingFireplacePreferenceId}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              options={values(preferenceTypes)}
                              label="Fireplace Preference?"
                            />
                          </div>
                          <div className="row mb-1">
                            <Field
                              component={ToggleSelect}
                              name="newDestination.purchasingGaragePreferenceId"
                              value={newDestination.purchasingGaragePreferenceId}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              options={values(preferenceTypes)}
                              label="Garage Preference?"
                            />
                          </div>
                          <div className="row mb-1">
                            <Field
                              component={ToggleSelect}
                              name="newDestination.purchasingBasementPreferenceId"
                              value={newDestination.purchasingBasementPreferenceId}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              options={values(preferenceTypes)}
                              label="Basement Preference?"
                            />
                          </div>
                        </Grid>
                        <Grid item xs={12} md={9}>
                          <div className="row align-center">
                            <Field
                              component={ToggleSelect}
                              name="newDestination.purchasingHasSchoolRequest"
                              value={newDestination.purchasingHasSchoolRequest}
                              onChange={this.handleSchoolRequestToggle('newDestination.purchasingSchoolRequest')}
                              onBlur={handleBlur}
                              options={YES_NO_OPTIONS}
                              label="School Request?"
                              className={classes.schoolRequestToggle}
                            />
                            {newDestination.purchasingHasSchoolRequest === true && (
                              <Field
                                component={TextField}
                                name="newDestination.purchasingSchoolRequest"
                                label="School Request Note"
                                value={newDestination.purchasingSchoolRequest}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                fullWidth
                              />
                            )}
                          </div>
                        </Grid>
                        <Grid item xs={12} md={9}>
                          <Field
                            component={TextField}
                            name="newDestination.purchasingImportantFeaturesSpecialRequests"
                            label="Important Features/Special Requests/Preferences"
                            value={newDestination.purchasingImportantFeaturesSpecialRequests}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            fullWidth
                          />
                        </Grid>
                      </Grid>
                    </ExpansionPanelDetails>
                  </ExpansionPanel>
                </Paper>
              </Grid>
              <Grid item xs={12}>
                <Paper classes={{ root: 'card-root' }}>
                  <ExpansionPanel expanded={!!newDestination.isRenting}>
                    <ExpansionPanelSummary>
                      <div className="row align-center">
                        <Typography variant="h4">Renting</Typography>
                        <Field
                          component={ToggleSelect}
                          name="newDestination.isRenting"
                          value={newDestination.isRenting}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          options={YES_NO_OPTIONS}
                          className={classes.headerToggle}
                          label="Is Renting"
                        />
                      </div>
                    </ExpansionPanelSummary>
                    <ExpansionPanelDetails>
                      <Grid container spacing={1}>
                        <Grid item xs={12}>
                          <div className="row mb-1">
                            <Field
                              component={TextField}
                              name="newDestination.rentingDateNeeded"
                              label="Date Needed"
                              className={classes.dateControl}
                              value={newDestination.rentingDateNeeded}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              type="date"
                            />
                          </div>
                          <div className="row mb-1">
                            <Field
                              component={TextField}
                              name="newDestination.rentingMinPrice"
                              label="Min Price"
                              value={newDestination.rentingMinPrice}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              allowDecimal={true}
                              numeric
                              prefix="$"
                            />
                            <Field
                              component={TextField}
                              name="newDestination.rentingMaxPrice"
                              label="Max Price"
                              value={newDestination.rentingMaxPrice}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              allowDecimal={true}
                              numeric
                              prefix="$"
                            />
                          </div>
                          <div className="row mb-1">
                            <Field
                              component={TextField}
                              name="newDestination.rentingBedroomsPreferred"
                              label="Bedroom Preference"
                              value={newDestination.rentingBedroomsPreferred}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              numeric
                            />
                            <Field
                              component={TextField}
                              name="newDestination.rentingBathroomsPreferred"
                              label="Bathroom Preference"
                              value={newDestination.rentingBathroomsPreferred}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              numeric
                            />
                          </div>
                        </Grid>
                        <Grid item xs={12} md={9}>
                          <div className="row align-center">
                            <Field
                              component={ToggleSelect}
                              name="newDestination.rentingHasSchoolRequest"
                              value={newDestination.rentingHasSchoolRequest}
                              onChange={this.handleSchoolRequestToggle('newDestination.rentingSchoolRequest')}
                              onBlur={handleBlur}
                              options={YES_NO_OPTIONS}
                              label="School Request?"
                              className={classes.schoolRequestToggle}
                            />
                            {newDestination.rentingHasSchoolRequest === true && (
                              <Field
                                component={TextField}
                                name="newDestination.rentingSchoolRequest"
                                label="School Request Note"
                                value={newDestination.rentingSchoolRequest}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                fullWidth
                              />
                            )}
                          </div>
                        </Grid>
                        <Grid item xs={12}>
                          <div className="row mb-1">
                            <Field
                              component={Checkbox}
                              name="newDestination.rentingAreaTour"
                              value={areaTourChecked}
                              onChange={this.handleAreaTourToggle}
                              onBlur={handleBlur}
                              label="Area Tour"
                              // Not a stored Formik value, so the adapter would otherwise render it indeterminate.
                              indeterminate={false}
                            />
                          </div>
                        </Grid>
                        {areaTourChecked && (
                          <Grid item xs={12}>
                            <div className="row mb-1">
                              <AdvancedSearchComponent
                                labelText="Area Tour Agent"
                                id="newDestination.rentingAreaTourAgentId"
                                displayValueAccessorKey={PARTNER_NAME_KEY}
                                saveIdValueAccessorKey={PARTNER_ID_KEY}
                                searchingCriteria={PARTNER_SEARCH_COLUMNS}
                                searchingCriteriaExtensions={PARTNER_SEARCH_COLUMN_EXTENSIONS}
                                searchingRows={partnerList}
                                value={areaTourPartnerDisplay}
                                setIdValue={this.handleAreaTourPartnerChange}
                                isReadOnly={Boolean(isReadOnly)}
                                variant="outlined"
                              />
                            </div>
                            <div className="row mb-1">
                              <Field
                                component={TextField}
                                name="newDestination.rentingAreaTourInitiationDate"
                                label="Area Tour Initiation Date"
                                className={classes.dateControl}
                                value={newDestination.rentingAreaTourInitiationDate}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                type="date"
                              />
                              <Field
                                component={TextField}
                                name="newDestination.rentingAreaTourCanceledDate"
                                label="Area Tour Canceled Date"
                                className={classes.dateControl}
                                value={newDestination.rentingAreaTourCanceledDate}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                type="date"
                              />
                              <Field
                                component={TextField}
                                name="newDestination.rentingAreaTourCompletedDate"
                                label="Area Tour Completion Date"
                                className={classes.dateControl}
                                value={newDestination.rentingAreaTourCompletedDate}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                type="date"
                              />
                            </div>
                          </Grid>
                        )}
                      </Grid>
                    </ExpansionPanelDetails>
                  </ExpansionPanel>
                </Paper>
              </Grid>
            </Grid>
          </ExpansionPanelDetails>
        </ExpansionPanel>
        <ConfirmationDialog
          open={showConfirmation}
          title="Disable Service?"
          confirmLabel="Yes"
          cancelLabel="No"
          onConfirm={this.handleDisableService}
          onCancel={() => this.setState({ showConfirmation: false })}
        >
          <Typography>
            If you disable this service, you will lose all data entered in this section and any timeline entries related to this service. <br /><br />Do you want to proceed?
          </Typography>
        </ConfirmationDialog>
        <ConfirmationDialog
          open={showChangeMoveType}
          title="Change Move Type?"
          confirmLabel="Change Move Type"
          cancelLabel="Cancel"
          onConfirm={() => { window.scrollTo({ top: 0, left: 0, behavior: 'smooth' }); this.setState({ showChangeMoveType: false }); }}
          onCancel={() => this.setState({ showChangeMoveType: false })}
        >
          <Typography>
            In order to initiate Destination Services, please change the move type from either Candidate or Intern.{' '}
            <br />
            <br />
            Do you want to proceed?
          </Typography>
        </ConfirmationDialog>
      </div>
    );
  }
}

NewDestinationForm.propTypes = {
  classes: PropTypes.object.isRequired,
  newDestination: PropTypes.object.isRequired,
  handleBlur: PropTypes.func.isRequired,
  handleChange: PropTypes.func.isRequired,
  metadata: PropTypes.object.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  partners: PropTypes.array,
  isReadOnly: PropTypes.bool,
};

export default compose(withStyles(styles))(NewDestinationForm);
